import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import PageHero from '../../components/feature/PageHero';
import { useCountUp } from '../../hooks/useCountUp';
import SEOHead from '../../components/feature/SEOHead';

// Componente individual de contador
function StatCounter({
  end,
  decimals = 0,
  prefix = '',
  suffix = '',
  label,
  sublabel,
  icon,
  color,
  duration = 2200,
}: {
  end: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  label: string;
  sublabel: string;
  icon: string;
  color: string;
  duration?: number;
}) {
  const { ref, formatted } = useCountUp({ end, decimals, duration });

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="flex flex-col items-center text-center p-8 bg-white rounded-2xl shadow-lg border border-stone-100 hover:shadow-xl transition-shadow duration-300"
    >
      <div className={`w-14 h-14 flex items-center justify-center rounded-full mb-5 ${color}`}>
        <i className={`${icon} text-2xl text-white`}></i>
      </div>
      <div className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-2 tabular-nums" style={{ fontFamily: "'Playfair Display', serif" }}>
        {prefix}{formatted}{suffix}
      </div>
      <div className="text-base font-semibold text-gray-800 mb-1">{label}</div>
      <div className="text-sm text-gray-500">{sublabel}</div>
    </div>
  );
}

export default function CasosDeSucessoPage() {
  const casos = [
    {
      id: 1,
      categoria: 'Seguro Automóvel',
      titulo: 'Redução de 42% em Frota Empresarial',
      cliente: 'Empresa de Transportes – Vitória da Conquista/BA',
      desafio: 'Empresa com frota de 28 veículos pagava R$ 156.000/ano em seguros sem análise técnica adequada. Coberturas duplicadas e franquias inadequadas geravam custos desnecessários.',
      solucao: 'Realizamos análise completa do perfil de risco da frota, renegociamos com 5 seguradoras diferentes e reestruturamos as coberturas eliminando redundâncias. Ajustamos franquias conforme histórico de sinistros.',
      resultados: [
        'Economia anual de R$ 65.520 (42%)',
        'Redução de 3 apólices para 1 apólice unificada',
        'Cobertura para danos a terceiros ampliada em 50%',
        'Tempo de resposta em sinistros reduzido de 15 para 3 dias'
      ],
      investimento: 'R$ 90.480/ano',
      roi: '42% de economia recorrente',
      prazo: '14 dias úteis',
      image: 'https://readdy.ai/api/search-image?query=modern%20commercial%20truck%20fleet%20parked%20organized%20rows%20professional%20transportation%20company%20vehicles%20clean%20simple%20background%20business%20logistics%20success&width=800&height=500&seq=ibxcaso01&orientation=landscape'
    },
    {
      id: 2,
      categoria: 'Vida Empresarial',
      titulo: 'Proteção Patrimonial para Sócios',
      cliente: 'Clínica Médica – 4 Sócios',
      desafio: 'Clínica com 4 sócios sem proteção financeira adequada em caso de falecimento. Risco de descapitalização e conflitos sucessórios que poderiam comprometer a continuidade do negócio.',
      solucao: 'Estruturamos seguro de vida empresarial com cobertura proporcional à participação societária de cada sócio. Negociamos condições especiais considerando o perfil de risco médico e incluímos cláusulas de invalidez.',
      resultados: [
        'Proteção de R$ 2,4 milhões distribuída entre os sócios',
        'Economia de 28% vs. cotação inicial',
        'Cobertura para invalidez permanente incluída',
        'Garantia de continuidade operacional da clínica'
      ],
      investimento: 'R$ 18.720/ano',
      roi: '28% de economia + segurança patrimonial',
      prazo: '11 dias úteis',
      image: 'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20professional%20doctors%20team%20business%20partners%20healthcare%20facility%20clean%20bright%20interior%20success%20partnership&width=800&height=500&seq=ibxcaso02&orientation=landscape'
    },
    {
      id: 3,
      categoria: 'Gestão de Contratos',
      titulo: 'Organização de 12 Apólices Empresariais',
      cliente: 'Construtora – Setor de Construção Civil',
      desafio: 'Empresa mantinha 12 apólices diferentes contratadas ao longo de 8 anos, sem controle de vencimentos, coberturas sobrepostas e pagamento de prêmios acima do mercado. Gestão financeira caótica.',
      solucao: 'Mapeamos todas as apólices vigentes, identificamos sobreposições, renegociamos com as seguradoras e consolidamos em 5 apólices estratégicas. Implementamos sistema de controle de vencimentos e renovações.',
      resultados: [
        'Redução de 12 para 5 apólices',
        'Economia anual de R$ 89.400 (35%)',
        'Eliminação de 7 coberturas redundantes',
        'Controle financeiro com alertas de vencimento'
      ],
      investimento: 'R$ 165.600/ano',
      roi: '35% de economia + controle gerencial',
      prazo: '21 dias úteis',
      image: 'https://readdy.ai/api/search-image?query=modern%20construction%20site%20building%20development%20professional%20contractor%20business%20success%20organized%20management%20clean%20background&width=800&height=500&seq=ibxcaso03&orientation=landscape'
    },
    {
      id: 4,
      categoria: 'Recuperação de Sinistros',
      titulo: 'Recuperação de R$ 340 mil em Sinistro Negado',
      cliente: 'Indústria de Alimentos – Bahia',
      desafio: 'Sinistro de R$ 340 mil negado pela seguradora sob alegação de "falta de manutenção preventiva". Cliente sem documentação técnica adequada e prestes a arcar com o prejuízo integral.',
      solucao: 'Analisamos a apólice, identificamos cláusulas contratuais favoráveis, reunimos documentação técnica complementar e conduzimos negociação direta com a seguradora apresentando laudos periciais.',
      resultados: [
        'Sinistro integralmente aprovado: R$ 340.000',
        'Prazo de pagamento: 18 dias após aprovação',
        'Revisão de cláusulas para evitar futuros problemas',
        'Cliente evitou prejuízo total'
      ],
      investimento: 'Honorários sobre êxito',
      roi: 'Recuperação de R$ 340.000',
      prazo: '32 dias úteis',
      image: 'https://readdy.ai/api/search-image?query=modern%20food%20processing%20industrial%20facility%20production%20line%20professional%20manufacturing%20business%20success%20quality%20control%20clean%20background&width=800&height=500&seq=ibxcaso04&orientation=landscape'
    },
    {
      id: 5,
      categoria: 'Consultoria Técnica',
      titulo: 'Análise de Riscos para Expansão Empresarial',
      cliente: 'Rede de Farmácias – Expansão para 3 Novas Unidades',
      desafio: 'Rede planejava abrir 3 novas unidades mas não tinha clareza sobre os riscos envolvidos e as coberturas necessárias. Risco de subcapitalização em caso de sinistros nas novas lojas.',
      solucao: 'Realizamos análise detalhada dos riscos de cada nova localização, dimensionamos coberturas para estoque, equipamentos e responsabilidade civil. Estruturamos apólices específicas para a expansão.',
      resultados: [
        'Mapeamento completo de riscos das 3 unidades',
        'Cobertura adequada para R$ 1,8 milhão em ativos',
        'Economia de 31% vs. cotação sem análise técnica',
        'Expansão protegida desde o primeiro dia'
      ],
      investimento: 'R$ 42.300/ano',
      roi: '31% de economia + segurança na expansão',
      prazo: '16 dias úteis',
      image: 'https://readdy.ai/api/search-image?query=modern%20pharmacy%20drugstore%20interior%20professional%20healthcare%20retail%20business%20clean%20bright%20organized%20shelves%20success%20expansion&width=800&height=500&seq=ibxcaso05&orientation=landscape'
    },
    {
      id: 6,
      categoria: 'Suporte e Acompanhamento',
      titulo: 'Acompanhamento Contínuo para Grupo Empresarial',
      cliente: 'Holding com 5 Empresas – Diversos Setores',
      desafio: 'Grupo empresarial com 5 empresas em setores diferentes (varejo, serviços, indústria) sem gestão unificada de seguros. Cada empresa contratava isoladamente, gerando custos elevados e coberturas inadequadas.',
      solucao: 'Implementamos gestão centralizada de todos os contratos de seguros do grupo. Renegociamos em bloco com seguradoras, unificamos vencimentos e criamos rotina de revisão trimestral.',
      resultados: [
        'Economia consolidada de R$ 127.000/ano (38%)',
        'Unificação de 18 apólices em 8 contratos estratégicos',
        'Revisão trimestral de coberturas e riscos',
        'Suporte dedicado para todas as empresas do grupo'
      ],
      investimento: 'R$ 207.000/ano',
      roi: '38% de economia + gestão profissional',
      prazo: 'Implementação em 28 dias úteis',
      image: 'https://readdy.ai/api/search-image?query=modern%20corporate%20business%20group%20holding%20company%20professional%20office%20building%20success%20diverse%20industries%20management%20clean%20background&width=800&height=500&seq=ibxcaso06&orientation=landscape'
    }
  ];

  const categorias = ['Todos', 'Seguro Automóvel', 'Vida Empresarial', 'Gestão de Contratos', 'Recuperação de Sinistros', 'Consultoria Técnica', 'Suporte e Acompanhamento'];
  const [filtroAtivo, setFiltroAtivo] = useState('Todos');

  const casosFiltrados = filtroAtivo === 'Todos'
    ? casos
    : casos.filter(caso => caso.categoria === filtroAtivo);

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Casos de Sucesso em Consultoria de Seguros",
    "description": "Conheça histórias reais de empresas que otimizaram seus seguros e economizaram com nossa consultoria especializada.",
    "url": "https://ibxbrasil.com.br/casos-de-sucesso"
  };

  return (
    <>
      <SEOHead
        title="Casos de Sucesso - Consultoria em Seguros | IBX Brasil"
        description="Veja como empresas de diversos setores economizaram e melhoraram suas coberturas com nossa consultoria especializada em seguros empresariais."
        canonical="https://ibxbrasil.com.br/casos-de-sucesso"
        ogImage="https://readdy.ai/api/search-image?query=successful%20business%20insurance%20case%20studies%20with%20happy%20corporate%20clients%20celebrating%20cost%20savings%20and%20improved%20coverage&width=1200&height=630&seq=ibx-casos-sucesso-og&orientation=landscape"
        schema={webPageSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Hero */}
        <PageHero
          badge="CASOS DE SUCESSO"
          title="Resultados Reais de Quem Profissionalizou a Contratação de Seguros"
          titleHighlight="Reais"
          titleHighlightColor="orange"
          subtitle="Conheça histórias reais de empresas e pessoas que reduziram custos, aumentaram proteção e conquistaram tranquilidade com a gestão profissional de riscos da IBX Brasil."
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20business%20success%20celebration%20team%20meeting%20modern%20corporate%20office%20insurance%20consulting%20achievement%20results%20growth%20charts%20graphs%20elegant%20deep%20teal%20orange%20accents%20sophisticated%20atmosphere&width=1440&height=700&seq=casos-hero-bg&orientation=landscape"
          overlayIntensity="medium"
          primaryCta={{
            text: 'Quero Ser o Próximo Caso',
            href: 'https://wa.me/5577988080505?text=Ol%C3%A1%2C%20gostaria%20de%20solicitar%20uma%20an%C3%A1lise%20gratuita.',
            icon: 'ri-whatsapp-line'
          }}
          secondaryCta={{
            text: 'Solicitar Análise Gratuita',
            href: '/#contato',
            icon: 'ri-file-list-3-line'
          }}
        />

        {/* Contadores Animados */}
        <section className="py-16 bg-gradient-to-br from-[#0F3D3E] to-[#0a2a2b]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-orange-400 text-sm font-semibold uppercase tracking-widest mb-3">
                Números que falam por si
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Impacto Real em Números
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCounter
                end={2.1}
                decimals={1}
                prefix="R$ "
                suffix="M+"
                label="Total Economizado"
                sublabel="em prêmios de seguros para clientes"
                icon="ri-money-dollar-circle-line"
                color="bg-orange-500"
                duration={2400}
              />
              <StatCounter
                end={340}
                suffix="+"
                label="Clientes Atendidos"
                sublabel="empresas e pessoas físicas protegidas"
                icon="ri-group-line"
                color="bg-[#1F7A4C]"
                duration={2000}
              />
              <StatCounter
                end={98}
                suffix="%"
                label="Satisfação dos Clientes"
                sublabel="índice de aprovação nos atendimentos"
                icon="ri-star-line"
                color="bg-orange-500"
                duration={1800}
              />
              <StatCounter
                end={25}
                suffix=" anos"
                label="de Experiência"
                sublabel="no mercado de gestão de riscos e seguros"
                icon="ri-award-line"
                color="bg-[#1F7A4C]"
                duration={1600}
              />
            </div>

            {/* Barra de destaque extra */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-shield-check-line', text: 'Sinistros recuperados com sucesso', value: '97%' },
                { icon: 'ri-time-line', text: 'Prazo médio de análise e contratação', value: '12 dias' },
                { icon: 'ri-percent-line', text: 'Economia média por cliente', value: '34%' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 bg-white/10 rounded-xl px-6 py-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-500/20 shrink-0">
                    <i className={`${item.icon} text-orange-400 text-xl`}></i>
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg leading-tight">{item.value}</p>
                    <p className="text-white/60 text-xs">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filtros */}
        <section className="py-12 bg-white border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-3">
              {categorias.map((categoria) => (
                <button
                  key={categoria}
                  onClick={() => setFiltroAtivo(categoria)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    filtroAtivo === categoria
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-stone-100 text-gray-700 hover:bg-stone-200'
                  }`}
                >
                  {categoria}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Casos de Sucesso */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="space-y-16">
              {casosFiltrados.map((caso, index) => (
                <div key={caso.id} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 items-center`}>
                  <div className="flex-1">
                    <div className="w-full h-80 rounded-2xl overflow-hidden shadow-xl bg-stone-200">
                      <img
                        src={caso.image}
                        alt={caso.titulo}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                        decoding="async"
                        width="800"
                        height="500"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full mb-4">
                      {caso.categoria}
                    </span>
                    <h3 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {caso.titulo}
                    </h3>
                    <p className="text-sm text-gray-500 mb-6 italic">{caso.cliente}</p>

                    <div className="space-y-5">
                      <div>
                        <h4 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-error-warning-line text-orange-500 text-lg"></i>
                          </div>
                          Desafio
                        </h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{caso.desafio}</p>
                      </div>

                      <div>
                        <h4 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-lightbulb-line text-orange-500 text-lg"></i>
                          </div>
                          Solução IBX Brasil
                        </h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{caso.solucao}</p>
                      </div>

                      <div>
                        <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-trophy-line text-orange-500 text-lg"></i>
                          </div>
                          Resultados Alcançados
                        </h4>
                        <ul className="space-y-2">
                          {caso.resultados.map((resultado, idx) => (
                            <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-700">
                              <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                                <i className="ri-checkbox-circle-fill text-orange-500 text-base"></i>
                              </div>
                              <span>{resultado}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-stone-200">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Investimento</p>
                          <p className="text-sm font-bold text-gray-900">{caso.investimento}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">ROI</p>
                          <p className="text-sm font-bold text-orange-600">{caso.roi}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Prazo</p>
                          <p className="text-sm font-bold text-gray-900">{caso.prazo}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-orange-500 to-orange-600">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Quer Resultados Como Estes?
            </h2>
            <p className="text-lg text-orange-50 mb-10 leading-relaxed">
              Solicite uma análise gratuita e descubra quanto você pode economizar com a gestão profissional de seguros da IBX Brasil.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/5577988080505"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-white text-orange-600 rounded-full font-semibold text-base hover:bg-orange-50 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-xl"></i>
                </div>
                Falar no WhatsApp
              </a>
              <a
                href="/contato"
                className="inline-flex items-center gap-2 px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl whitespace-nowrap"
              >
                Solicitar Análise Gratuita
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
